/**
 * Translation Helpers
 * Reusable helper functions for language normalization and translation with specific language codes
 */

/**
 * Normalizes language codes for translation
 * - Maps 'en-us' and 'en-uk' to 'en'
 * - Returns other language codes as-is (de, ar, etc.)
 * @param {string} languageCode - The language code from request payload
 * @returns {string} Normalized language code
 */
export const normalizeLanguageCode = (languageCode) => {
  if (!languageCode) return 'en';
  
  const normalized = languageCode.toLowerCase();
  
  // Map all English variants (en-us, en-uk, en-gb, etc.) to en
  if (normalized.startsWith('en-')) {
    return 'en';
  }
  
  // Return as-is for other languages (de, ar, etc.)
  return normalized;
};

/**
 * Ensures language resources are loaded for a given language code
 * @param {object} i18n - i18next instance
 * @param {string} languageCode - Language code to load
 * @returns {Promise<void>} Promise that resolves when resources are loaded
 */
export const ensureLanguageLoaded = async (i18n, languageCode) => {
  if (!i18n || !languageCode) return;
  
  const normalizedLang = normalizeLanguageCode(languageCode);
  
  // Check if resources are already loaded
  if (i18n.hasResourceBundle && i18n.hasResourceBundle(normalizedLang, 'translations')) {
    return;
  }
  
  // Load the language resources by changing language temporarily
  // This triggers http-backend to load the resources
  try {
    const currentLang = i18n.language;
    // Change language to trigger loading
    await i18n.changeLanguage(normalizedLang);
    // Wait a bit to ensure resources are loaded
    // Check if resources are now loaded
    let attempts = 0;
    while (!i18n.hasResourceBundle(normalizedLang, 'translations') && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    // Change back to original language if needed
    if (currentLang !== normalizedLang) {
      await i18n.changeLanguage(currentLang);
    }
  } catch (error) {
    console.error('Error loading language resources:', error);
  }
};

/**
 * Gets translation for a specific language code using i18next
 * @param {object} i18n - i18next instance
 * @param {string} key - Translation key
 * @param {string} languageCode - Language code from request payload
 * @returns {string} Translated text for the specified language
 */
export const getTranslation = (i18n, key, languageCode) => {
  if (!i18n || !key) {
    console.warn('getTranslation: Missing i18n or key', { i18n: !!i18n, key });
    return key;
  }
  
  const normalizedLang = normalizeLanguageCode(languageCode);
  
  // Try to get translation with the specified language
  // Use getFixedT which should work even if resources aren't fully loaded yet
  // but will still try to use the correct language
  try {
    const tFixed = i18n.getFixedT(normalizedLang);
    const translated = tFixed(key);
    
    // If we got the key back (translation not found), try direct t call
    if (translated === key || !translated) {
      return i18n.t(key, { lng: normalizedLang });
    }
    
    return translated;
  } catch (error) {
    console.error('Translation error:', error);
    // Fallback to direct t call
    return i18n.t(key, { lng: normalizedLang });
  }
};

/**
 * Gets translation for a specific language code using t function
 * This is an alternative when you have the t function from withTranslation HOC
 * @param {function} t - Translation function from i18next
 * @param {string} key - Translation key
 * @param {string} languageCode - Language code from request payload
 * @param {object} i18n - i18next instance (optional, used to get available languages)
 * @returns {string} Translated text for the specified language
 */
export const getTranslationFromT = (t, key, languageCode) => {
  const normalizedLang = normalizeLanguageCode(languageCode);
  // Use t function with lng option to get translation in specific language
  return t(key, { lng: normalizedLang });
};

